import { useMemo } from 'react';
import { Platform, StyleSheet, View, ViewProps } from 'react-native';

import {
  IconInfoFill,
  IconSodium,
  Text,
  Wordmark,
  XStack,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';
import { DeliveryFeesApply } from '@fhs-legacy/frontend/src/components/delivery-fees-apply';

import { ProductTray } from '../product-detail-layout/product-tray';

import { buildProductSummaryA11yLabel } from './product-summary-a11y-label';
import { productSummaryRegionWebProps } from './product-summary-web-a11y-props';
import { useProductDisplayName } from './use-product-display-name';

type HeroProps = {
  displayName: string;
  description: string;
  variant: string;
  cals: string;
  imageUrl: string;
  heroNutritionalInformationTile?: React.ReactNode;
  showSodiumDisclaimer?: boolean;
  hasCustomizationsContent?: boolean;
  isStaticDetails: boolean;
  showUnavailabilityDisclaimer?: boolean;
};

export const HeroVariant = ({
  cals,
  description,
  displayName,
  imageUrl,
  variant,
  heroNutritionalInformationTile,
  showSodiumDisclaimer,
  isStaticDetails,
  hasCustomizationsContent,
  showUnavailabilityDisclaimer,
}: HeroProps) => {
  const mqStyles = useMqStyles();
  const formattedDisplayName = useProductDisplayName({ displayName, isStaticDetails });

  const productSummaryAccessibilityLabel = useMemo(() => {
    let label = buildProductSummaryA11yLabel({
      formattedDisplayName,
      variant,
      cals,
      description,
      showSodiumDisclaimer,
    });
    if (showUnavailabilityDisclaimer) {
      const disclaimerText = `We're sorry, this product is currently ${
        isStaticDetails ? 'unavailable.' : 'unavailable at the selected store. '
      }`;
      label = `${label}. ${disclaimerText}`;
    }
    return label;
  }, [
    cals,
    description,
    formattedDisplayName,
    isStaticDetails,
    showSodiumDisclaimer,
    showUnavailabilityDisclaimer,
    variant,
  ]);

  return (
    <>
      <Spacer style={styles.minHeight24} />
      <View style={styles.content}>
        {!hasCustomizationsContent && (
          <Wordmark style={mqStyles.logo} accessible={false} accessibilityElementsHidden />
        )}
        <View style={styles.gap12}>
          <View
            accessible
            accessibilityLabel={productSummaryAccessibilityLabel}
            {...(productSummaryRegionWebProps(productSummaryAccessibilityLabel) ?? {})}
          >
            <View
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
            >
              <View>
                <Text weight="bold" style={[mqStyles.name]}>
                  {formattedDisplayName}
                </Text>
                {cals && (
                  <Text.Paragraph
                    size="lg"
                    style={[styles.textCenter, mqStyles.cals, { alignItems: 'center' }]}
                  >
                    {variant} • {cals}
                    {showSodiumDisclaimer && (
                      <>
                        {' • '}
                        <IconSodiumDisclaimer />
                      </>
                    )}
                  </Text.Paragraph>
                )}
              </View>
              {showUnavailabilityDisclaimer && (
                <UnavailabilityDisclaimer isStaticDetails={isStaticDetails} />
              )}
              {description ? (
                <Text.Paragraph size="md" style={[styles.textCenter, mqStyles.description]}>
                  {description}
                </Text.Paragraph>
              ) : null}
            </View>
          </View>
          <View style={[styles.centerHorizontal, { pointerEvents: 'auto' }]}>
            <DeliveryFeesApply />
          </View>
        </View>
      </View>
      {heroNutritionalInformationTile && (
        <View style={mqStyles.heroNutritionalInformationTile}>
          {heroNutritionalInformationTile}
        </View>
      )}
      <Spacer style={styles.minHeight24} />
      <ProductTray
        source={imageUrl}
        accessibilityLabel={`${formattedDisplayName} product image`}
      />
    </>
  );
};

function Spacer(props: ViewProps) {
  return <View {...props} style={[styles.spacer, props.style]} />;
}

function IconSodiumDisclaimer() {
  const iconSodiumSize = useMqSelect({ $ltDesktop: 16 }, 20);
  return <IconSodium size={iconSodiumSize} style={{ marginBottom: -5 }} />;
}

function UnavailabilityDisclaimer({ isStaticDetails }: { isStaticDetails?: boolean }) {
  const mqStyles = useMqStyles();

  const text = `We're sorry, this product is currently ${
    isStaticDetails ? 'unavailable.' : 'unavailable at the selected store. '
  }`;

  return (
    <XStack style={mqStyles.unavailabilityDisclaimer}>
      <IconInfoFill size={20} />
      <Text.Paragraph size="md" style={mqStyles.textSubTitle}>
        {text}
      </Text.Paragraph>
    </XStack>
  );
}

const styles = StyleSheet.create({
  content: {
    alignItems: 'center',
    paddingHorizontal: 24,
    marginHorizontal: 'auto',
  },
  spacer: {
    flexGrow: 1,
  },
  minHeight24: {
    minHeight: 24,
  },
  textCenter: {
    textAlign: 'center',
    lineHeight: 22.4,
  },
  gap12: {
    gap: 12,
  },
  centerHorizontal: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
});

const useMqStyles = createMqStyles({
  logo: {
    $base: {
      display: 'flex',
      height: 56,
      alignSelf: 'center',
      marginBottom: 24,
    },
    $gteDesktop: {
      display: 'none',
    },
  },
  description: {
    $gteDesktop: {
      fontSize: 'clamp(12px, 1.6vh, 16px)' as any,
      paddingVertical: 24,
    },
  },
  cals: {
    $gteDesktop: {
      fontSize: 'clamp(14px, 2.1vh, 20px)' as any,
      paddingTop: 4,
    },
  },
  name: {
    $base: {
      fontSize: 28,
      lineHeight: 28 * 1.4,
      textAlign: 'center',
    },
    $gteDesktop: {
      fontSize: 'clamp(20px, 5vh, 48px)' as any,
      lineHeight: `clamp(30px, 5vh, ${48 * 1.4}px)` as any,
    },
  },
  heroNutritionalInformationTile: {
    $ltDesktop: {
      paddingHorizontal: 16,
      paddingTop: 16,
    },
    $gteDesktop: {
      paddingVertical: 28,
    },
  },
  unavailabilityDisclaimer: {
    $base: {
      backgroundColor: tokens.colors.$houseLight,
      justifyContent: 'center',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 12,
      paddingHorizontal: 24,
      borderRadius: 8,
      marginHorizontal: 16,
      // Padding is resulting in a different space for web
      ...(Platform.OS === 'web' && {
        paddingHorizontal: 12,
      }),
    },
  },
  textSubTitle: {
    $base: {
      textAlign: 'center',
    },
  },
});
